/**
 * Slugs.
 *
 * This is a PORT, not an implementation. `public.slugify()` in migration 001
 * generates the slug that becomes a business's permanent public URL, and this
 * function exists so the client can show the user the URL they are about to get
 * before the insert happens. If the two disagree, the preview lies — and worse,
 * anything the client computes and stores (a draft link, an OG image path)
 * points at a page that does not exist.
 *
 * The SQL, for reference:
 *
 *   trim(both '-' from
 *     regexp_replace(
 *       regexp_replace(
 *         translate(
 *           replace(replace(lower(coalesce(p_text,'')),'æ','ae'),'ß','ss'),
 *           'øåéèêëáàâäóòôöíìîïúùûüçñ',
 *           'oaeeeeaaaaooooiiiiuuuucn'),
 *         '[^a-z0-9]+', '-', 'g'),
 *       '-{2,}', '-', 'g'))
 *
 * Norwegian letters are transliterated, never stripped: "aerlig-elektriker" and
 * "rlig-elektriker" are different pages to a search engine, and only one of them
 * is the business's name.
 */

/**
 * The `translate()` map from the SQL, character for character. æ and ß are not
 * here because they expand to TWO characters, which `translate()` cannot do —
 * they are replaced first, in both implementations.
 */
const TRANSLITERATIONS: ReadonlyMap<string, string> = new Map(
  Object.entries({
    ø: 'o',
    å: 'a',
    é: 'e',
    è: 'e',
    ê: 'e',
    ë: 'e',
    á: 'a',
    à: 'a',
    â: 'a',
    ä: 'a',
    ó: 'o',
    ò: 'o',
    ô: 'o',
    ö: 'o',
    í: 'i',
    ì: 'i',
    î: 'i',
    ï: 'i',
    ú: 'u',
    ù: 'u',
    û: 'u',
    ü: 'u',
    ç: 'c',
    ñ: 'n',
  }),
)

/** Fallback slug base, matching `assign_business_slug()` in migration 003. */
export const FALLBACK_BUSINESS_SLUG = 'bedrift'

/**
 * `slugify('Ærlig Elektriker')` → `'aerlig-elektriker'`.
 *
 * Byte-for-byte identical to `public.slugify()`. Change one, change the other,
 * and update the tests that pin the shared cases.
 */
export function slugify(text: string | null | undefined): string {
  const lowered = (text ?? '').toLowerCase()

  // Multi-character expansions first — see the note above.
  const expanded = lowered.replace(/æ/gu, 'ae').replace(/ß/gu, 'ss')

  let transliterated = ''
  for (const character of expanded) {
    transliterated += TRANSLITERATIONS.get(character) ?? character
  }

  return transliterated
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/-{2,}/gu, '-')
    .replace(/^-+|-+$/gu, '')
}

/**
 * The slug a business will actually receive, given the slugs already taken.
 * Mirrors `assign_business_slug()`: an empty base becomes "bedrift", and a
 * collision gets a numeric suffix starting at 2 rather than failing the insert.
 *
 * The database remains authoritative — it holds the unique index and it sees
 * every row, which a client filtered by RLS does not. This is for the preview
 * field, so the number the user sees is usually the number they get.
 */
export function uniqueSlug(text: string | null | undefined, taken: Iterable<string>): string {
  const base = slugify(text) || FALLBACK_BUSINESS_SLUG
  const used = new Set(taken)

  if (!used.has(base)) return base

  let n = 1
  let candidate = base
  while (used.has(candidate)) {
    n += 1
    candidate = `${base}-${n}`
  }
  return candidate
}

/** Is this string already a valid slug — i.e. is `slugify` a no-op on it? */
export function isSlug(value: string): boolean {
  return value.length > 0 && slugify(value) === value
}
